import { formatISO, parseISO } from 'date-fns';
import { Mapper } from '../../../mappers';
import {
  Quota,
  QuotaActivity,
  QuotaActivityFieldOption,
  QuotaRevenue,
  QuotaRevenueFieldOption,
  QuotaForm,
  QuotaYear,
  SalesTeam,
} from '../../../models';
import {
  FieldOption,
  InteractionType,
  KeyAndDisplay,
  ValueAndDisplay,
} from '../models';
import { Quota as OctopusQuota, QuotaParentKey } from '../models/target';
import {
  QuotaActivityFieldOptionsResponse,
  QuotaActivity as OctopusQuotaActivity,
} from '../models/target-activity';
import {
  QuotaRevenueFieldOptionsResponse,
  QuotaRevenue as OctopusQuotaRevenue,
} from '../models/target-revenue';
import { ConfigurationMapper } from './configuration';
import { Octopus } from '../..';
import { SecurityAccessItem } from '../models/security-access';

export class QuotaActivityFieldOptionMapper extends ConfigurationMapper<
  QuotaActivityFieldOptionsResponse,
  QuotaActivityFieldOption
> {
  from(source: QuotaActivityFieldOptionsResponse): QuotaActivityFieldOption {
    const quotaActivity = source.QuotaActivity?.FieldOptions;

    return {
      categories:
        quotaActivity?.InteractionCategory?.Data?.map(this.mapFieldOption).sort(
          (first, second) => first.name.localeCompare(second.name),
        ) ?? [],
      types:
        quotaActivity?.InteractionType?.Data.map(this.mapInteractionType)
          .filter(
            (item) =>
              item.type !== Octopus.DefaultInteractions.NOTE &&
              item.type !== Octopus.DefaultInteractions.DOCUMENT,
          )
          .sort((first, second) =>
            first.display.localeCompare(second.display),
          ) ?? [],
      readAccess:
        quotaActivity?.SecAccess?.Read?.map(this.mapSecurityAccess) ?? [],
      fullAccess:
        quotaActivity?.SecAccess?.Write?.map(this.mapSecurityAccess) ?? [],
    };
  }
}

export class QuotaRevenueFieldOptionMapper extends ConfigurationMapper<
  QuotaRevenueFieldOptionsResponse,
  QuotaRevenueFieldOption
> {
  from(source: QuotaRevenueFieldOptionsResponse): QuotaRevenueFieldOption {
    const quotaRevenue = source.QuotaRevenue?.FieldOptions;

    return {
      revenueTypes:
        quotaRevenue?.RevenueType?.Data?.map(this.mapFieldOption) ?? [],
      readAccess:
        quotaRevenue?.SecAccess?.Read?.map(this.mapSecurityAccess) ?? [],
      fullAccess:
        quotaRevenue?.SecAccess?.Write?.map(this.mapSecurityAccess) ?? [],
    };
  }
}

export abstract class QuotaMapper<
  Source extends OctopusQuota,
  Target extends Quota,
> extends Mapper<Source[], Target[]> {
  constructor(private readonly startMonth: number) {
    super();
  }

  override from(source: Source[]): Target[] {
    return source.map((item) => {
      return this.mapItem(item);
    });
  }

  mapItem(source: Source): Target {
    const milestones = source.Milestones;

    const target = {
      key: source.Key,
      fiscalYear: source.FiscalYear,
      quarter1: 0,
      quarter2: 0,
      quarter3: 0,
      quarter4: 0,
      month0: 0,
      month1: 0,
      month2: 0,
      month3: 0,
      month4: 0,
      month5: 0,
      month6: 0,
      month7: 0,
      month8: 0,
      month9: 0,
      month10: 0,
      month11: 0,
      total: 0,
      security: {
        read: source.SecStatus?.CanRead,
        insert: source.SecStatus?.CanCreate,
        update: source.SecStatus?.CanUpdate,
        delete: source.SecStatus?.CanDelete,
      },
    } as Quota;

    const quota = new QuotaYear(source.FiscalYear, this.startMonth);

    milestones.forEach((milestone) => {
      const date = parseISO(milestone.StartDate);
      const month = date.getMonth();
      const amount = milestone.Amount as ValueAndDisplay<number>;
      const property = `month${month}`;
      const quarter = quota.getQuarterForMonth(month);

      target[property] = amount.Value;

      if (quarter) {
        (target[`quarter${quarter.number}`] as number) += amount.Value;
      }
      target.total += amount.Value;
    });

    return target as Target;
  }
}

export class QuotaActivityMapper extends QuotaMapper<
  OctopusQuotaActivity,
  QuotaActivity
> {
  override mapItem(source: OctopusQuotaActivity): QuotaActivity {
    const parentKey = source.ParentKey as QuotaParentKey;
    const target = super.mapItem(source);
    const interactionType = source.InteractionType as InteractionType;
    const interactionCategories = source.InteractionCategory as KeyAndDisplay[];

    target.parentKey = parentKey.UidKey.Value;
    target.parentName = parentKey.UidKey.DisplayValue;
    target.interactionType = interactionType?.DisplayValue;
    target.interactionCategory = interactionCategories?.length
      ? interactionCategories[0].DisplayValue
      : '';

    return target;
  }
}

export class QuotaRevenueMapper extends QuotaMapper<
  OctopusQuotaRevenue,
  QuotaRevenue
> {
  override from(
    source: OctopusQuotaRevenue[],
    teams?: SalesTeam[],
  ): QuotaRevenue[] {
    return source.map((item) => {
      return this.mapItem(item, teams);
    });
  }

  override mapItem(
    source: OctopusQuotaRevenue,
    teams?: SalesTeam[],
  ): QuotaRevenue {
    const parentKey = source.ParentKey as QuotaParentKey;
    const revenueType = source.RevenueType as FieldOption;
    const target = super.mapItem(source);

    if (parentKey.UidKey) {
      target.parentKey = parentKey.UidKey.Value;
      target.parentName = parentKey.UidKey.DisplayValue;
      target.teams = teams ? this.mapTeams(parentKey.UidKey.Value, teams) : '';
    } else if (parentKey.SalesTeamKey) {
      target.parentKey = parentKey.SalesTeamKey.Value;
      target.parentName = target.teams = source.Parent.SalesTeam.DisplayName;
    }

    target.revenueType = revenueType?.DisplayValue;

    return target;
  }

  mapTeams(userKey: string, teams: SalesTeam[]): string {
    const salesteams = teams.filter((team) => {
      const members = team.members.filter((member) => member.id === userKey);
      return members.length > 0;
    });
    return salesteams.length > 0
      ? salesteams.map((team) => team.display).join(', ')
      : '';
  }
}

export abstract class QuotaFormMapper<
  Target extends OctopusQuota,
> extends Mapper<QuotaForm, Target> {
  override from(source: QuotaForm): Target {
    return {
      Key: source.key,
      ParentKey: source.parent,
      FiscalYear: source.year,
      Milestones:
        source.quota?.months.map((month) => {
          return {
            Key: null,
            StartDate: formatISO(month.start, { representation: 'date' }),
            EndDate: formatISO(month.end, { representation: 'date' }),
            Amount: month.total,
          };
        }) ?? [],
      SecAccess: {
        Read: [{ Key: { Value: source.readAccess } } as SecurityAccessItem],
        Write: [{ Key: { Value: source.fullAccess } } as SecurityAccessItem],
      },
    } as Target;
  }

  to(target: Target, startMonth: number): QuotaForm {
    const year = target.FiscalYear;
    const parentKey = target.ParentKey as QuotaParentKey;
    const milestones = target.Milestones;
    const quota = new QuotaYear(year, startMonth);

    milestones.forEach((milestone) => {
      const date = parseISO(milestone.StartDate);
      const monthNumber = date.getMonth();
      const amount = milestone.Amount as ValueAndDisplay<number>;
      const month = quota.months.find((m) => m.number === monthNumber);

      if (month) {
        month.total = amount.Value;
      }
    });

    return {
      key: target.Key,
      year,
      parent:
        parentKey.UidKey?.Value ??
        parentKey.SalesTeamKey?.Value ??
        parentKey.Value,
      interactionType: '',
      interactionCategory: '',
      revenueType: '',
      quota,
      readAccess: target.SecAccess?.Read[0].Key.Value ?? '',
      fullAccess: target.SecAccess?.Write[0].Key.Value ?? '',
    };
  }
}

export class QuotaRevenueFormMapper extends QuotaFormMapper<OctopusQuotaRevenue> {
  override from(source: QuotaForm): OctopusQuotaRevenue {
    return {
      ...super.from(source),
      RevenueType: source.revenueType?.length ? source.revenueType : null,
    };
  }

  override to(target: OctopusQuotaRevenue, startMonth: number): QuotaForm {
    const revenueType = target.RevenueType as FieldOption;

    return {
      ...super.to(target, startMonth),
      revenueType: revenueType?.Key ?? '',
    };
  }
}

export class QuotaActivityFormMapper extends QuotaFormMapper<OctopusQuotaActivity> {
  override from(source: QuotaForm): OctopusQuotaActivity {
    return {
      ...super.from(source),
      InteractionType: source.interactionType,
      InteractionCategory: source.interactionCategory?.length
        ? source.interactionCategory
        : null,
    };
  }

  override to(target: OctopusQuotaActivity, startMonth: number): QuotaForm {
    const interactionType = target.InteractionType as InteractionType;
    const interactionCategories = target.InteractionCategory as KeyAndDisplay[];

    return {
      ...super.to(target, startMonth),
      interactionType: interactionType?.Key ?? '',
      interactionCategory: interactionCategories?.length
        ? interactionCategories[0].Key
        : '',
    };
  }
}
